import { makeAutoObservable, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { TableStore } from 'shared/ui/TableNew/store/tableStore'
import { logger } from 'shared/lib'
import { SingletonRequest } from 'shared/api/lib'
import { toastStore } from 'shared/ui'
import { InboxesApi } from 'entities/Inbox'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { type IResponseInboxGroup } from 'entities/Inbox/api/types'
import { GroupInbox } from 'entities/Inbox/model/GroupInbox'
import { RowGroupInbox } from 'entities/Inbox/model/Table/RowGroupInbox'
import { type InboxesStore } from 'pages/settings/pages/inboxes/store/inboxesStore'
import { GroupInboxFormStore } from 'pages/settings/pages/inboxes/pages/inboxesView/store/groupInboxFormStore'

export class GroupInboxesViewStore {
  private _inboxesStore: InboxesStore
  private _tableStore: TableStore<RowGroupInbox>
  private _itemsMap: Map<number, GroupInbox> = new Map()
  private _firstLoading = true
  private _search = ''
  private _groupInboxFormStore: GroupInboxFormStore

  constructor(_inboxesStore: InboxesStore, private _entities: IEntitiesStore) {
    this._inboxesStore = _inboxesStore
    this._tableStore = new TableStore({
      key: 'groupInboxesTable',
      element: 'groupInboxes',
      sortOrder: 'asc',
      sortBy: 'name',
    })
    this._groupInboxFormStore = new GroupInboxFormStore(this, this._inboxesStore, _entities)

    makeAutoObservable(this)
  }

  init = () => {
    this.loadData()
  }

  get tableStore() {
    return this._tableStore
  }

  get firstLoading() {
    return this._firstLoading
  }

  get loading() {
    return this._tableStore.loading
  }

  get entities() {
    return this._entities
  }

  get search() {
    return this._search
  }

  get groupInboxFormStore() {
    return this._groupInboxFormStore
  }

  get items() {
    return Array.from(this._itemsMap.values()).filter((item) => {
      if (this._inboxesStore.shouldRestrictListAccess()) {
        return item.teamsIds.some((teamId) => {
          const inbox = this._inboxesStore.inboxesViewStore.getItem(teamId)
          return !!inbox
        })
      }
      return item
    })
  }

  get itemsRows() {
    return this.items.map((item) => new RowGroupInbox(item))
  }

  setItem = (data: IResponseInboxGroup) => {
    this._itemsMap.set(data.id, new GroupInbox(data, this._entities))
  }

  setSearch = (value: string) => {
    this._search = value
  }

  get filteredRows() {
    let filtered = this.itemsRows

    if (this._search.trim()) {
      const searchTerm = this._search.toLowerCase().trim()
      filtered = filtered.filter(
        (row) =>
          row.origin.name.toLowerCase().includes(searchTerm) ||
          row.members.some((member) => member.name.toLowerCase().includes(searchTerm))
      )
    }

    return filtered
  }

  get sortedRows() {
    const { sortBy, sortOrder } = this._tableStore
    const filtered = this.filteredRows

    if (!sortBy) return filtered

    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.origin.name.localeCompare(b.origin.name)
        case 'members':
          return a.inboxesCount - b.inboxesCount
        case 'created_at':
          return new Date(a.createdAt || '').getTime() - new Date(b.createdAt || '').getTime()
        default:
          return 0
      }
    })

    return sortOrder === 'asc' ? sorted : sorted.reverse()
  }

  private _fetchGroupInboxes = async () => {
    try {
      runInAction(() => {
        this._tableStore.setLoading(true)
      })
      const { data } = await InboxesApi.getGroupInboxes()

      runInAction(() => {
        this._itemsMap.clear()
        data.forEach((item) => {
          this.setItem(item)
        })
      })
    } catch (error) {
      logger.error(error)
    } finally {
      runInAction(() => {
        this._tableStore.setLoading(false)
        this._firstLoading = false
      })
    }
  }

  loadData = new SingletonRequest(this._fetchGroupInboxes).request

  handleDeleteGroupInboxModal = (id: number) => {
    const modalId = nanoid()
    modalStore.addModal({
      id: modalId,
      type: ModalTypeList.ALERT,
      title: 'Remove group inbox?',
      primaryAction: {
        text: 'Remove',
        onAction: async () => {
          try {
            await this.handleDeleteGroupInbox(id)
            modalStore.closeModal(modalId)
          } catch (e) {
            logger.error(e)
          }
        },
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: () => {
          modalStore.closeModal(modalId)
        },
      },
    })
  }

  handleDeleteGroupInbox = async (id: number) => {
    try {
      await InboxesApi.deleteTeamsGroupsById(id)
      this._itemsMap.delete(id)
      toastStore.add({
        type: 'success',
        title: 'Group inbox removed',
      })
    } catch (error) {
      logger.error(error)
    }
  }
}
